import React, { useState } from "react";
import { Menu, X, ArrowRight, CheckCircle2 } from "lucide-react";

const FEATURES = [
  {
    title: "Program Dashboard & Athlete Oversight",
    description:
      "Give coaches and administrators a single place to manage programs, teams, rosters, and performance data.",
    bullets: [
      "Admin + coach dashboards",
      "Program and team management",
      "Role-based access for staff",
    ],
    image: "/screenshots/admin-dashboard.svg",
  },
  {
    title: "SWOT Analysis & Season Priorities",
    description:
      "Capture strengths, weaknesses, opportunities, and threats so every athlete knows what matters most.",
    bullets: [
      "SWOT hub for coaches",
      "Priority tracking by athlete",
      "Clear development focus",
    ],
    image: "/screenshots/swot.png",
  },
  {
    title: "Skill & S&C Workouts",
    description:
      "Deliver targeted skill and strength work so athletes train with purpose.",
    bullets: [
      "Skill workouts for development",
      "Strength & conditioning plans",
      "Progress tracked by session",
    ],
    image: "/screenshots/skill-workouts.png",
  },
  {
    title: "Combine & Performance Metrics",
    description:
      "Track combine scores and measurable progress across your roster.",
    bullets: [
      "Combine scoreboards",
      "Performance snapshots",
      "Year-over-year comparisons",
    ],
    image: "/screenshots/combine-scores.png",
  },
  {
    title: "Schedules & Calendar",
    description:
      "Keep athletes and staff aligned with a shared calendar view for training and events.",
    bullets: [
      "Program calendar",
      "Workout and tryout events",
      "Always-visible weekly planning",
    ],
    image: "/screenshots/schedule.png",
  },
  {
    title: "Marketplace & Program Discovery",
    description:
      "Help athletes find programs, tryouts, and opportunities in one place.",
    bullets: [
      "Marketplace search",
      "Program profiles",
      "Location-aware discovery",
    ],
    image: "/screenshots/marketplace.png",
  },
];

const ScreenshotFrame = ({ image, title }) => (
  <div className="rounded-3xl border border-slate-200 bg-white shadow-sm overflow-hidden">
    <div className="bg-white flex items-center justify-center text-slate-400 text-sm font-semibold">
      {image ? (
        <img
          src={image}
          alt={`${title} screenshot`}
          className="w-full h-auto"
          loading="lazy"
        />
      ) : (
        <span className="py-20">Add screenshot</span>
      )}
    </div>
  </div>
);

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const brandLogo = `${process.env.PUBLIC_URL}/NuvioLogo.png?v=20260314c`;

  const navLinks = [
    {
      name: "About",
      href: "/home/about",
      children: [{ name: "Why Nuvio?", href: "/home/why-nuvio" }],
    },
    { name: "For Coaches", href: "/home/programs" },
    { name: "For Athletes", href: "/home/athletes" },
    { name: "Features", href: "/home/features" },
    { name: "Pricing", href: "/home/pricing" },
    { name: "Case Studies", href: "/home/case-studies" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="/home" className="flex items-center gap-2">
          <img
            src={brandLogo}
            alt="Nuvio"
            className="w-8 h-8 rounded-lg object-cover shadow-sm"
          />
          <span className="text-xl font-bold tracking-tight text-slate-900">
            Nuvio<span className="text-blue-600">Sport</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div key={link.name} className="relative group">
                <a
                  href={link.href}
                  className="text-sm font-semibold text-slate-600 hover:text-blue-600 transition-colors"
                >
                  {link.name}
                </a>
                <div className="absolute left-0 top-full pt-3 opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto transition-all">
                  <div className="bg-white border border-slate-100 shadow-xl rounded-xl p-3 min-w-[180px]">
                    {link.children.map((child) => (
                      <a
                        key={child.name}
                        href={child.href}
                        className="block px-3 py-2 text-sm font-semibold text-slate-700 rounded-lg hover:bg-slate-50 hover:text-blue-600"
                      >
                        {child.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <a
                key={link.name}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  link.name === "Features" ? "text-blue-600" : "text-slate-600 hover:text-blue-600"
                }`}
              >
                {link.name}
              </a>
            )
          )}
          <a href="/login" className="text-sm font-bold text-slate-700 hover:text-blue-600">
            Log In
          </a>
          <a
            href="/create-account"
            className="px-6 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
          >
            Create Account
          </a>
        </div>

        <button
          className="lg:hidden text-slate-700 p-2 hover:bg-slate-50 rounded-lg transition-colors"
          onClick={() => setIsOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div
        className={`lg:hidden absolute top-20 left-0 w-full bg-white border-b border-slate-100 transition-all duration-300 ease-in-out overflow-hidden ${
          isOpen ? "max-h-[520px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-6 py-8 flex flex-col gap-6 shadow-xl">
          {navLinks.map((link) => (
            <div key={link.name} className="flex flex-col gap-2">
              <a
                href={link.href}
                onClick={() => setIsOpen(false)}
                className="text-lg font-bold text-slate-900 hover:text-blue-600"
              >
                {link.name}
              </a>
              {link.children && (
                <div className="ml-4 flex flex-col gap-2">
                  {link.children.map((child) => (
                    <a
                      key={child.name}
                      href={child.href}
                      onClick={() => setIsOpen(false)}
                      className="text-sm font-semibold text-slate-600 hover:text-blue-600"
                    >
                      {child.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <hr className="border-slate-100" />
          <a
            href="/login"
            onClick={() => setIsOpen(false)}
            className="w-full py-4 text-center font-bold text-slate-700 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
          >
            Log In
          </a>
          <a
            href="/create-account"
            onClick={() => setIsOpen(false)}
            className="w-full py-4 text-center font-bold text-white bg-blue-600 rounded-xl shadow-lg shadow-blue-600/20 hover:bg-blue-700 transition-all"
          >
            Create Account
          </a>
        </div>
      </div>
    </nav>
  );
};

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      <section className="pt-36 pb-14 px-6 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-bold uppercase tracking-widest mb-6">
            Features
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4">
            Everything your program needs to develop talent at scale.
          </h1>
          <p className="text-slate-500 text-lg max-w-3xl mx-auto">
            Nuvio Sport connects coaches, athletes, and administrators with tools that make
            player development measurable, repeatable, and aligned with your system.
          </p>
        </div>
      </section>

      <section className="pb-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto space-y-16">
          {FEATURES.map((feature) => (
            <div
              key={feature.title}
              className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center"
            >
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h2>
                <p className="text-slate-600 mb-4">{feature.description}</p>
                <ul className="space-y-2">
                  {feature.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 size={18} className="text-blue-600 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <ScreenshotFrame image={feature.image} title={feature.title} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="pb-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Ready to align your program?</h2>
          <p className="text-slate-600 mb-8">
            Give your staff clarity, give your athletes direction, and give your program a
            repeatable development pipeline.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/create-account"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-xl shadow-blue-600/20"
            >
              Create Account <ArrowRight size={18} />
            </a>
            <a
              href="/home/pricing"
              className="w-full sm:w-auto px-8 py-4 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold hover:bg-slate-50 transition-all"
            >
              View Pricing
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
